import DOMPurify from 'dompurify';

const ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'em',
  'u',
  's',
  'ul',
  'ol',
  'li',
  'blockquote',
  'code',
  'pre',
  'span',
  'a',
  'h1',
  'h2',
  'h3'
] as const;

const ALLOWED_ATTR = {
  a: ['href', 'target', 'rel', 'title', 'class'],
  span: ['class', 'data-mention', 'data-mention-id', 'data-mention-label']
} as const;

export const sanitizeHtml = (value: string | undefined | null): string => {
  if (!value) return '';
  // Preserve links and ensure they have proper attributes
  return DOMPurify.sanitize(value, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    RETURN_TRUSTED_TYPE: false,
    ALLOW_ARIA_ATTR: false,
    FORBID_TAGS: ['script', 'style'],
    FORBID_ATTR: ['style'],
    // Ensure links are preserved and clickable
    ADD_ATTR: ['target', 'rel', 'href', 'class', 'data-mention', 'data-mention-id', 'data-mention-label'],
    // Keep data attributes if needed
    KEEP_CONTENT: true
  });
};

export const isProbablyHtml = (value: string): boolean => {
  return /<\/?[a-z][\s\S]*>/i.test(value.trim());
};

export const plainTextToHtml = (value: string): string => {
  if (!value) return '';

  const escaped = value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  const lines = escaped.split(/\r?\n/);
  const html = lines
    .map((line) => {
      const trimmed = line.trim();
      if (!trimmed) {
        return '<p><br /></p>';
      }
      return `<p>${trimmed}</p>`;
    })
    .join('');

  return sanitizeHtml(html || '<p><br /></p>');
};

export const ensureHtml = (value: string | undefined | null): string => {
  if (!value) return '';
  return isProbablyHtml(value) ? sanitizeHtml(value) : plainTextToHtml(value);
};

export const toPlainText = (value: string | undefined | null): string => {
  if (!value) return '';
  if (typeof window !== 'undefined') {
    const container = window.document.createElement('div');
    container.innerHTML = sanitizeHtml(value);
    return container.textContent || container.innerText || '';
  }

  return sanitizeHtml(value).replace(/<[^>]*>/g, ' ');
};

// URL regex pattern - matches http://, https:// URLs
const URL_REGEX = /(https?:\/\/[^\s<>"']+)/gi;

// Function to convert plain text URLs to clickable links
export const processLinksInHtml = (html: string): string => {
  if (!html) return '';
  
  // Use DOM if available for accurate processing
  if (typeof window !== 'undefined') {
    const tempDiv = window.document.createElement('div');
    tempDiv.innerHTML = html;

    // Get all text nodes and process them
    const walker = window.document.createTreeWalker(
      tempDiv,
      NodeFilter.SHOW_TEXT,
      null
    );

    const textNodes: Text[] = [];
    let node: Node | null;
    while ((node = walker.nextNode())) {
      const parent = node.parentElement;
      // Skip if already inside an anchor tag
      if (parent && parent.tagName.toLowerCase() !== 'a') {
        textNodes.push(node as Text);
      }
    }

    textNodes.forEach((textNode) => {
      const text = textNode.textContent || '';
      const urlMatches = Array.from(text.matchAll(URL_REGEX));
      
      if (urlMatches.length === 0) return;

      const fragment = window.document.createDocumentFragment();
      let lastIndex = 0;

      urlMatches.forEach((match) => {
        if (match.index === undefined) return;

        // Add text before the URL
        if (match.index > lastIndex) {
          fragment.appendChild(
            window.document.createTextNode(text.substring(lastIndex, match.index))
          );
        }

        // Create link element
        const link = window.document.createElement('a');
        link.href = match[0];
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.className = 'text-link';
        link.textContent = match[0];
        fragment.appendChild(link);

        lastIndex = match.index + match[0].length;
      });

      // Add remaining text
      if (lastIndex < text.length) {
        fragment.appendChild(
          window.document.createTextNode(text.substring(lastIndex))
        );
      }

      // Replace the text node
      if (textNode.parentNode) {
        textNode.parentNode.replaceChild(fragment, textNode);
      }
    });

    return tempDiv.innerHTML;
  }

  // Server-side fallback: simple regex replacement
  return html.replace(URL_REGEX, (url, offset, string) => {
    // Check if URL is already in a link by looking at context
    const before = string.substring(Math.max(0, offset - 50), offset);
    const after = string.substring(offset + url.length, Math.min(string.length, offset + url.length + 50));
    
    // If we see <a before and no </a> between, it's already a link
    if (before.includes('<a ') && !before.includes('</a>')) {
      const openTagPos = before.lastIndexOf('<a ');
      const closeTagPos = after.indexOf('</a>');
      if (closeTagPos >= 0 || openTagPos > (before.lastIndexOf('</a>') || -1)) {
        return url;
      }
    }
    
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-link">${url}</a>`;
  });
};

const RAW_MENTION_REGEX = /@[\w\s_.-]+(?=[\s<>]|$|&nbsp;|[,.;!?:])/gi;
const CONTEXT_MENTION_REGEX = /(^|[^\w@])(@[\w\s_.-]+)(?=\s|$|[^@\w\s_.-])/g;

const isInsideHtmlTag = (source: string, index: number): boolean => {
  const lastLt = source.lastIndexOf('<', index);
  if (lastLt === -1) return false;
  const lastGt = source.lastIndexOf('>', index);
  return lastLt > lastGt;
};

const isAlreadyWrappedMention = (source: string, index: number): boolean => {
  const lastSpanOpen = source.lastIndexOf('<span', index);
  if (lastSpanOpen === -1) return false;
  const lastSpanClose = source.lastIndexOf('</span>', index);
  if (lastSpanClose > lastSpanOpen) return false;
  const closeIdx = source.indexOf('>', lastSpanOpen);
  if (closeIdx === -1 || closeIdx < lastSpanOpen) return false;
  const openTag = source.slice(lastSpanOpen, closeIdx);
  return /\bclass\s*=\s*["'][^"']*\bmention\b/i.test(openTag);
};

const wrapMentionsWithRegex = (input: string): string => {
  if (!input) return '';

  return input.replace(
    CONTEXT_MENTION_REGEX,
    (match, prefix, mention, offset) => {
      const mentionStart = offset + prefix.length;
      if (isInsideHtmlTag(input, mentionStart) || isAlreadyWrappedMention(input, mentionStart)) {
        return match;
      }
      return `${prefix}<span class="mention">${mention}</span>`;
    }
  );
};

// Function to highlight @mentions in HTML
export const processMentionsInHtml = (html: string): string => {
  if (!html) return '';

  let processed = html;

  const hasDomApis =
    typeof window !== 'undefined' &&
    typeof window.document !== 'undefined' &&
    typeof window.document.createTreeWalker === 'function' &&
    typeof window.NodeFilter !== 'undefined';

  if (hasDomApis) {
    try {
      const tempDiv = window.document.createElement('div');
      tempDiv.innerHTML = html;

      const walker = window.document.createTreeWalker(
        tempDiv,
        window.NodeFilter.SHOW_TEXT,
        null
      );

      const textNodes: Text[] = [];
      let node: Node | null;
      while ((node = walker.nextNode())) {
        const parent = node.parentElement;
        if (parent && parent.classList?.contains('mention')) continue;
        textNodes.push(node as Text);
      }

      textNodes.forEach((textNode) => {
        const text = textNode.textContent || '';
        const mentionMatches = Array.from(text.matchAll(RAW_MENTION_REGEX));

        if (mentionMatches.length === 0) return;

        const fragment = window.document.createDocumentFragment();
        let lastIndex = 0;

        mentionMatches.forEach((match) => {
          if (match.index === undefined) return;

          if (match.index > lastIndex) {
            fragment.appendChild(
              window.document.createTextNode(text.substring(lastIndex, match.index))
            );
          }

          const mention = window.document.createElement('span');
          mention.className = 'mention';
          mention.textContent = match[0];
          fragment.appendChild(mention);

          lastIndex = match.index + match[0].length;
        });

        if (lastIndex < text.length) {
          fragment.appendChild(
            window.document.createTextNode(text.substring(lastIndex))
          );
        }

        if (textNode.parentNode) {
          textNode.parentNode.replaceChild(fragment, textNode);
        }
      });

      processed = tempDiv.innerHTML;
    } catch {
      processed = html;
    }
  }

  return wrapMentionsWithRegex(processed);
};

// Combined function to process both links and mentions for display
export const processHtmlForDisplay = (html: string): string => {
  if (!html) return '';
  
  // First ensure it's HTML
  let processed = ensureHtml(html);
  
  // Process links first (they might contain mentions)
  processed = processLinksInHtml(processed);
  
  // Then process mentions (they might be in or outside links)
  processed = processMentionsInHtml(processed);
  
  // Final sanitization for security
  return sanitizeHtml(processed);
};
